import { putRequest } from "./httpRequest";

export interface NotificationResource {
  // Notifications use their numeric id; earned badges come through the same
  // feed with a namespaced string id ("badge-123") so the two can't collide.
  id: number | string;
  href: string;
  mood: string;
  title: string;
  body: string;
  avatar: string;
  target_name: string;
  created_at: string;
  created_at_timestamp: string;
  category: string;
  badge_url: string;
  opened: boolean;
}

// Community notifications carry the post and reply in the query string, but
// /community is a single-page app that reads them from sessionStorage on boot
// rather than from the URL. Anything else is a plain navigation.
const navigateTo = (href: string) => {
  const url = new URL(href, window.location.origin);
  const isCommunity =
    url.origin === window.location.origin && url.pathname === "/community";

  if (isCommunity) {
    sessionStorage.setItem(
      "communityNotificationTarget",
      JSON.stringify({
        postId: url.searchParams.get("post_id"),
        replyId:
          url.searchParams.get("reply_comment_id") ||
          url.searchParams.get("comment_id"),
      }),
    );
    window.location.href = "/community";
    return;
  }

  window.location.href = href;
};

// Marks the notification seen, then follows it. Shared by the bell dropdown and
// the alerts page so the two can't drift. A failed mark still navigates -- the
// unread dot lingering is a far smaller problem than a dead link.
export const openNotification = (
  notification: Pick<NotificationResource, "id" | "href" | "opened">,
  onMarked: () => void,
) => {
  if (notification.opened) {
    navigateTo(notification.href);
    return;
  }

  putRequest(`/api/notifications/${notification.id}/mark_as_seen.json`, {})
    .then(() => {
      onMarked();
      navigateTo(notification.href);
    })
    .catch(() => {
      navigateTo(notification.href);
    });
};

type Group = "today" | "yesterday" | "week" | "older";

export const GROUP_LABELS: Record<Group, string> = {
  today: "Today",
  yesterday: "Yesterday",
  week: "Earlier this week",
  older: "Older",
};

export const GROUP_ORDER: Group[] = ["today", "yesterday", "week", "older"];

// Calendar-day comparisons, not rolling 24-hour windows: something at 11pm
// last night should read as "Yesterday" at 9am, not "Today".
export const notificationGroup = (timestamp: string, now = new Date()): Group => {
  const created = new Date(timestamp);

  if (Number.isNaN(created.getTime())) {
    return "older";
  }

  const startOf = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  // Rounded, not floored: across a DST boundary the gap between two midnights
  // is 23 or 25 hours, which would otherwise slide everything a day out.
  const daysBack = Math.round((startOf(now) - startOf(created)) / 86400000);

  if (daysBack <= 0) return "today";
  if (daysBack === 1) return "yesterday";
  if (daysBack < 7) return "week";

  return "older";
};
